import React from 'react';
import { Check, CalendarDays, RefreshCw, Search, X } from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { Input } from '@/Modules/Core/Components/shadcn/input';
import { cn } from '@/lib/utils';

const showNativeDatePicker = (event) => {
  if (typeof event.currentTarget.showPicker === 'function') {
    event.currentTarget.showPicker();
  }
};

const handleNativeDateKeyDown = (event) => {
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
    event.preventDefault();
    showNativeDatePicker(event);
    return;
  }

  if (event.key === 'Tab' || event.key === 'Shift' || event.key === 'Escape') {
    return;
  }

  event.preventDefault();
};

const getFilterButtonClassName = (statusFilter, optionId) => {
  const isActive = statusFilter === optionId;

  if (!isActive) {
    return 'border-border/40 bg-background/70 text-muted-foreground hover:border-border/60 hover:text-foreground dark:bg-background/55';
  }

  if (optionId === 'success') {
    return 'border-emerald-400/45 bg-emerald-500/12 text-emerald-700 shadow-sm dark:text-emerald-200';
  }

  if (optionId === 'failed') {
    return 'border-rose-400/45 bg-rose-500/12 text-rose-700 shadow-sm dark:text-rose-200';
  }

  return 'border-primary/35 bg-primary/12 text-primary shadow-sm dark:text-primary-foreground';
};

const getStatusIcon = (statusId) => {
  if (statusId === 'success') {
    return Check;
  }

  if (statusId === 'failed') {
    return X;
  }

  return null;
};

const DateFilterInput = ({ id, label, value, onChange, ariaLabel }) => (
  <div className="relative flex items-center gap-2 rounded-xl border border-border/40 bg-background/70 px-3 py-2 dark:bg-background/55">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </span>
    <input
      id={id}
      type="date"
      value={value}
      onClick={showNativeDatePicker}
      onFocus={showNativeDatePicker}
      onKeyDown={handleNativeDateKeyDown}
      onChange={(event) => onChange(event.target.value)}
      className="h-6 w-[132px] border-0 bg-transparent px-0 pr-7 text-[13px] text-foreground outline-none [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
      aria-label={ariaLabel}
    />
    <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
  </div>
);

const WebHookLogFeedFilters = ({
  filters,
  onFilterChange,
  onImmediateFilterChange,
  onRefresh,
  refreshing = false,
  statusOptions = [],
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="webhook-search"
          value={filters.searchTerm}
          onChange={(event) => onFilterChange('searchTerm', event.target.value)}
          placeholder="Search webhook log ID, author, repo, status, commit date, or created date"
          className="h-11 rounded-2xl border-border/45 bg-background/55 pl-9"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <DateFilterInput
          id="webhook-from-date"
          label="From"
          value={filters.fromDate}
          onChange={(value) => onImmediateFilterChange('fromDate', value)}
          ariaLabel="Filter webhook logs from date"
        />

        <DateFilterInput
          id="webhook-to-date"
          label="To"
          value={filters.toDate}
          onChange={(value) => onImmediateFilterChange('toDate', value)}
          ariaLabel="Filter webhook logs to date"
        />

        <Button
          type="button"
          size="icon"
          variant="outline"
          className="border-border/40 bg-transparent hover:bg-accent/20"
          aria-label="Refresh webhook logs"
          title="Refresh webhook logs"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
        </Button>
      </div>
    </div>

    <div className="flex flex-wrap gap-2">
      {statusOptions.map((option) => {
        const Icon = getStatusIcon(option.id);

        return (
          <button
            key={option.id}
            type="button"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors',
              getFilterButtonClassName(filters.status, option.id)
            )}
            onClick={() => onImmediateFilterChange('status', option.id)}
          >
            {Icon ? <Icon className="h-4 w-4" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default WebHookLogFeedFilters;
